import styled from "@emotion/styled";
import { useRef, forwardRef, useImperativeHandle, KeyboardEvent, HTMLAttributes } from "react";
import { KalturaESearchHistory } from "kaltura-typescript-client/api/types/KalturaESearchHistory";
import { IconButton, MenuItem, Tooltip, menuItemClasses } from "@kaltura/ds-react-components";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { Close16Icon } from "@kaltura/ds-react-icons";
import { MenuList, Paper } from "@mui/material";
import { autocompleteClasses } from "@mui/material/Autocomplete";
import { UseAutocompleteRenderedOption } from "@mui/material/useAutocomplete";
import { VisibleFocusStyle } from "@kaltura/ds-react-theme";

export interface SuggestionsListProps {
    suggestions: KalturaESearchHistory[];
    onItemSelect: (keyword: string) => void;
    onItemRemove: (keyword: string) => void;

    /**
     * useAutocomplete props
     */
    listboxProps: HTMLAttributes<HTMLUListElement>;
    getOptionProps: (option: UseAutocompleteRenderedOption<KalturaESearchHistory>) => HTMLAttributes<HTMLLIElement>;

    /**
     * key down(arrow left) callback
     */
    onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
}

const StyledWrapperAnchor = styled("div")(({ theme }) => ({
    position: "relative",
}));

const StyledWrapper = styled("div")(({ theme }) => ({
    position: "absolute",
    zIndex: 1300,
    right: 0,
    bottom: 0,
    top: 0,
    left: 0,
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    width: 205,
    // copied from the styling for Menu in ui-kit
    backgroundColor: theme.kaltura.palette.surfaces.elevated,
    color: theme.kaltura.palette.tone1,
    borderStyle: "solid",
    borderColor: theme.kaltura.palette.tone6,
    borderWidth: "1px",
    borderRadius: theme.kaltura.shape.roundness1,
    marginTop: theme.spacing(1),
    boxShadow: theme.kaltura.elevations.medium,
}));

const StyledMenuList = styled(MenuList)(({ theme }) => ({
    // copied from the styling for Menu in ui-kit
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
    color: theme.kaltura.palette.tone1,
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
    [`&.kms-ds-suggestion-list-item .${menuItemClasses.title}`]: {
        marginRight: theme.spacing(4),
    },

    // copied from the styling for MenuItem in ui-kit
    [`&.${autocompleteClasses.focused}, &.${menuItemClasses.focusVisible}`]: {
        backgroundColor: theme.kaltura.palette.tone6,
        ...VisibleFocusStyle,
    },
}));

/**
 * button to remove suggestion from list
 */
const RemoveButton = forwardRef<HTMLButtonElement, { searchTerm: string; onClick: (term: string) => void }>(
    ({ searchTerm, onClick }, ref) => {
        return (
            <Tooltip title={translate("remove item from search history: %1", [searchTerm])}>
                <StyledIconButton
                    ref={ref}
                    aria-label={translate("remove item from search history: %1", [searchTerm])}
                    onClick={(e) => {
                        e.stopPropagation();
                        onClick(searchTerm);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === "Space") {
                            e.stopPropagation();
                            e.preventDefault();
                            onClick(searchTerm);
                        }
                    }}
                >
                    <Close16Icon />
                </StyledIconButton>
            </Tooltip>
        );
    }
);

export interface SuggestionsListHandle {
    setOptionRemoveFocus: (searchTerm: string) => void;
}

/**
 * list of suggested search terms
 */
export const SuggestionsList = forwardRef<SuggestionsListHandle, SuggestionsListProps>(
    ({ listboxProps, getOptionProps, suggestions, onItemSelect, onItemRemove, onKeyDown }, ref) => {
        const classNamePrefix = "kms-ds-global-search-suggestion-list";

        // ref array of options' remove buttons
        const removeButtonsRef = useRef<Array<HTMLButtonElement | null>>(new Array(suggestions.length));
        const addRemoveButtonsRef = (element: HTMLButtonElement | null, index: number) => {
            removeButtonsRef.current[index] = element;
        };

        /**
         * custom ref handle to allow focus on a specific option's
         * remove button
         */
        useImperativeHandle(ref, () => {
            return {
                // set focus on the remove button
                setOptionRemoveFocus(searchTerm: string) {
                    // find the highlighted option index
                    const optionIndex = suggestions.findIndex(
                        (option) => (option as KalturaESearchHistory).searchTerm === searchTerm
                    );
                    // set focus on the `remove` button of the given search suggestion
                    removeButtonsRef.current[optionIndex]?.focus();
                },
            };
        }, []);

        /**
         * yield focus when the user click left arrow
         * rightArrow (on the input) caused us to receive focus
         */
        const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "ArrowLeft") {
                event.stopPropagation();
                event.preventDefault();
                onKeyDown?.(event);
            }
        };

        /* using Paper and MenuList directly because using Menu blocks the background and typing becomes disabled */
        return (
            <StyledWrapperAnchor>
                <StyledWrapper>
                    <StyledPaper className={classNamePrefix}>
                        <StyledMenuList {...listboxProps}>
                            {suggestions.map((suggestion, index) => {
                                const optionProps = getOptionProps({
                                    option: suggestion,
                                    index,
                                });

                                return (
                                    <StyledMenuItem
                                        classes={{ root: `${classNamePrefix}-item` }}
                                        aria-label={suggestion.searchTerm}
                                        icon={
                                            <RemoveButton
                                                searchTerm={suggestion.searchTerm}
                                                onClick={onItemRemove}
                                                ref={(el) => addRemoveButtonsRef(el, index)}
                                            />
                                        }
                                        {...optionProps}
                                        onClick={(event: React.MouseEvent<HTMLLIElement>) => {
                                            onItemSelect(suggestion.searchTerm);
                                            optionProps.onClick?.(event);
                                        }}
                                        onKeyDown={handleKeyDown}
                                    >
                                        {suggestion.searchTerm}
                                    </StyledMenuItem>
                                );
                            })}
                        </StyledMenuList>
                    </StyledPaper>
                </StyledWrapper>
            </StyledWrapperAnchor>
        );
    }
);

export default SuggestionsList;
